import { useState, useEffect, useRef } from 'react'
import * as api from '../../lib/api'
import { useToast } from '../../contexts/ToastContext'
import { IconAlertCircle, IconClock, IconHourglass, IconPlay, IconStop } from '../Icons'
import Modal from '../Modal'
import './ShiftTimer.css'

export default function ShiftTimer({ profile, salaryConfig, isDemo = false }) {
  const toast = useToast()
  const [shiftData, setShiftData] = useState(null) // Запись в schedule
  const [loading, setLoading] = useState(true)
  const [starting, setStarting] = useState(false)
  const [ending, setEnding] = useState(false)
  const [elapsed, setElapsed] = useState(0) // Секунды с начала смены
  const [breakElapsed, setBreakElapsed] = useState(0) // Секунды текущего перерыва
  const [businessHours, setBusinessHours] = useState(null)
  const [showEndConfirm, setShowEndConfirm] = useState(false)
  const [timeUntilOpen, setTimeUntilOpen] = useState(null) // Минут до открытия
  const [todayOps, setTodayOps] = useState({ acceptance: 0, issued: 0, returned: 0 }) // Операции за сегодня
  const timerRef = useRef(null)

  const loadOperations = async () => {
    if (!profile?.id) return
    try {
      const today = new Date().toISOString().split('T')[0]
      const ops = await api.getWorkLogs('all', { startDate: today, endDate: today, employeeId: profile.id })
      const acceptance = (ops || []).filter(o => o.type === 'acceptance').reduce((s, o) => s + (o.count || 0) + (o.count_late || 0), 0)
      const issued = (ops || []).filter(o => o.type === 'issued').reduce((s, o) => s + (o.count || 0), 0)
      const returned = (ops || []).filter(o => o.type === 'returns').reduce((s, o) => s + (o.count || 0), 0)
      setTodayOps({ acceptance, issued, returned })
    } catch(e) { console.error('Ops load error:', e) }
  }

  // Загружаем бизнес-часы и текущую смену при монтировании
  useEffect(() => {
    if (!profile?.id) return
    setLoading(true)
    
    const loadData = async () => {
      if (isDemo) {
        setBusinessHours({ open_time: '09:00', close_time: '21:00', timezone: 'Europe/Moscow' })
        setShiftData(null)
        setTodayOps({ acceptance: 150, issued: 120, returned: 10 })
        setLoading(false)
        return
      }
      try {
        const biz = await api.getPvzBusinessHours()
        setBusinessHours(biz)
        
        const data = await api.getActiveShift(profile.id)
        setShiftData(data)
        
        await loadOperations()
        
        if (data?.started_at && !data?.ended_at) {
          // Смена активна — считаем elapsed
          const start = new Date(data.started_at)
          setElapsed(Math.floor((Date.now() - start.getTime()) / 1000))
          
          const activeBreak = (data.breaks || []).find(b => !b.end)
          if (activeBreak) {
            setBreakElapsed(Math.floor((Date.now() - new Date(activeBreak.start).getTime()) / 1000))
          }
        }
      } catch (err) {
        console.error('Ошибка загрузки смены:', err)
      } finally {
        setLoading(false)
      }
    }
    
    loadData()

    if (isDemo) return

    window.addEventListener('operationsUpdated', loadOperations)
    window.addEventListener('shiftStarted', loadData)
    return () => {
      window.removeEventListener('operationsUpdated', loadOperations)
      window.removeEventListener('shiftStarted', loadData)
    }
  }, [profile?.id, isDemo])

  // Таймер — обновляем каждую секунду
  useEffect(() => {
    if (shiftData?.started_at && !shiftData?.ended_at) {
      timerRef.current = setInterval(() => {
        const start = new Date(shiftData.started_at)
        setElapsed(Math.floor((Date.now() - start.getTime()) / 1000))
        
        const activeBreak = (shiftData?.breaks || []).find(b => !b.end)
        if (activeBreak) {
          setBreakElapsed(Math.floor((Date.now() - new Date(activeBreak.start).getTime()) / 1000))
        } else {
          setBreakElapsed(0)
        }
      }, 1000)
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
    }
  }, [shiftData?.started_at, shiftData?.ended_at, shiftData?.breaks])

  // Проверяем, можно ли начать смену (до open_time)
  useEffect(() => {
    if (!businessHours || isDemo) return
    
    const checkTime = () => {
      const now = new Date()
      const formatter = new Intl.DateTimeFormat('en-CA', {
        timeZone: businessHours.timezone || 'Europe/Moscow',
        hour: '2-digit', minute: '2-digit',
        hour12: false,
      })
      const parts = formatter.formatToParts(now)
      const h = parseInt(parts.find(p => p.type === 'hour')?.value || '0', 10)
      const m = parseInt(parts.find(p => p.type === 'minute')?.value || '0', 10)
      
      const [openH, openM] = (businessHours.open_time || '09:00').split(':').map(Number)
      const nowMinutes = h * 60 + m
      const openMinutes = openH * 60 + openM
      
      if (nowMinutes < openMinutes) {
        setTimeUntilOpen(openMinutes - nowMinutes)
      } else {
        setTimeUntilOpen(null)
      }
    }
    
    checkTime()
    const interval = setInterval(checkTime, 30000) // Проверяем каждые 30 секунд
    return () => clearInterval(interval)
  }, [businessHours, isDemo])

  const isActive = shiftData?.started_at && !shiftData?.ended_at
  const isFinished = shiftData?.started_at && shiftData?.ended_at
  
  // Перерывы
  const breaksList = shiftData?.breaks || []
  const activeBreak = breaksList.find(b => !b.end)
  const isOnBreak = !!activeBreak
  
  // Расчет суммарного времени завершенных перерывов в минутах
  const totalCompletedBreaksMs = breaksList.reduce((acc, b) => {
    if (b.start && b.end) {
      return acc + (new Date(b.end) - new Date(b.start))
    }
    return acc
  }, 0)
  const totalCompletedBreaksMinutes = Math.floor(totalCompletedBreaksMs / 60000)
  
  const maxBreaksCount = 4
  const maxBreaksDurationMinutes = 60
  const breaksCount = breaksList.length
  
  const isBreaksLimitReached = (breaksCount >= maxBreaksCount || totalCompletedBreaksMinutes >= maxBreaksDurationMinutes) && !isOnBreak

  // Чистые отработанные секунды для расчета live-earnings и таймера
  const getCleanElapsedSeconds = () => {
    if (!shiftData?.started_at) return 0
    let breaksMs = 0
    for (const b of breaksList) {
      if (b.start && b.end) {
        breaksMs += new Date(b.end) - new Date(b.start)
      } else if (b.start && !b.end) {
        breaksMs += Date.now() - new Date(b.start).getTime()
      }
    }
    const totalMs = Date.now() - new Date(shiftData.started_at).getTime()
    // Вычитаем только перерывы, превышающие 60 минут (3600000 мс)
    const deductibleBreakMs = Math.max(0, breaksMs - 3600000)
    const cleanMs = totalMs - deductibleBreakMs
    return Math.max(0, Math.floor(cleanMs / 1000))
  }

  const cleanSeconds = getCleanElapsedSeconds()

  // Форматирование таймера
  const formatTime = (totalSeconds) => {
    const h = Math.floor(totalSeconds / 3600)
    const m = Math.floor((totalSeconds % 3600) / 60)
    const s = totalSeconds % 60
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
  }

  // Расчёт текущей зарплаты за смену (стриминг)
  const calculateLiveEarnings = () => {
    if (!salaryConfig || cleanSeconds === 0) return 0
    const hoursElapsed = cleanSeconds / 3600
    
    const earnings = api.calculateDayEarnings(hoursElapsed, profile?.role || 'employee', salaryConfig, {
      acceptance: todayOps.acceptance,
      issued: todayOps.issued,
      acceptanceLate: 0,
      returned: todayOps.returned
    })
    
    return earnings.total
  }

  const handleStart = async () => {
    setStarting(true)
    if (isDemo) {
      setTimeout(() => {
        setShiftData({
          id: 'demo-shift',
          started_at: new Date().toISOString(),
          ended_at: null,
          breaks: []
        })
        setElapsed(0)
        setBreakElapsed(0)
        setTimeUntilOpen(null)
        setStarting(false)
        toast.success('Смена начата (Демо-режим)')
      }, 300)
      return
    }
    try {
      const data = await api.startShift(profile.id, profile.role || 'employee')
      setShiftData(data)
      setElapsed(0)
      setBreakElapsed(0)
      setTimeUntilOpen(null)
      if (data._autoCorrected) {
        toast.success('Смена начата! Время скорректировано на 09:00')
      } else {
        toast.success('Смена начата!')
      }
    } catch (err) {
      console.error('Ошибка начала смены:', err)
      toast.error(err.message || 'Ошибка начала смены')
    } finally {
      setStarting(false)
    }
  }

  const handleEnd = async () => {
    setShowEndConfirm(false)
    setEnding(true)
    if (isDemo) {
      setTimeout(() => {
        const endedShift = {
          ...shiftData,
          ended_at: new Date().toISOString(),
          hours: 8
        }
        setShiftData(endedShift)
        if (timerRef.current) clearInterval(timerRef.current)
        setEnding(false)
        toast.success('Смена завершена! Отработано: 8 ч. (Демо-режим)')
      }, 300)
      return
    }
    try {
      const data = await api.endShift(profile.id)
      setShiftData(data)
      if (timerRef.current) clearInterval(timerRef.current)
      toast.success(`Смена завершена! Отработано: ${data.hours} ч.`)
    } catch (err) {
      console.error('Ошибка завершения смены:', err)
      toast.error(err.message || 'Ошибка завершения смены')
    } finally {
      setEnding(false)
    }
  }

  const handleStartBreak = async () => {
    if (isDemo) {
      setShiftData({
        ...shiftData,
        breaks: [...(shiftData.breaks || []), { start: new Date().toISOString(), end: null }]
      })
      toast.success('Вы ушли на перерыв. Время работы приостановлено (Демо-режим).')
      return
    }
    try {
      const data = await api.startBreak(profile.id)
      setShiftData(data)
      toast.success('Вы ушли на перерыв. Время работы приостановлено.')
    } catch (err) {
      console.error('Ошибка начала перерыва:', err)
      toast.error(err.message || 'Ошибка начала перерыва')
    }
  }

  const handleEndBreak = async () => {
    if (isDemo) {
      const updatedBreaks = (shiftData.breaks || []).map(b => !b.end ? { ...b, end: new Date().toISOString() } : b)
      setShiftData({ ...shiftData, breaks: updatedBreaks })
      toast.success('Вы вернулись с перерыва. Смена возобновлена (Демо-режим)!')
      return
    }
    try {
      const data = await api.endBreak(profile.id)
      setShiftData(data)
      toast.success('Вы вернулись с перерыва. Смена возобновлена!')
    } catch (err) {
      console.error('Ошибка завершения перерыва:', err)
      toast.error(err.message || 'Ошибка завершения перерыва')
    }
  }

  const liveEarnings = calculateLiveEarnings()
  const formatMoney = (n) => new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', maximumFractionDigits: 0 }).format(n)

  // Превышение регламента перерыва (более 15 минут)
  const isBreakCritical = isOnBreak && breakElapsed >= 12 * 60
  const isBreakOvertime = isOnBreak && breakElapsed > 15 * 60

  const getBreakBtnStyle = () => {
    if (!isOnBreak) return {}
    const pct = Math.min(breakElapsed / (15 * 60), 1) // 0 to 1
    const hue = 140 - pct * 140 // 140 (green) to 0 (red)
    const saturation = 70 + pct * 10 // 70% to 80%
    const lightness = 45 + pct * 5 // 45% to 50%
    return {
      background: `hsl(${hue}, ${saturation}%, ${lightness}%)`,
      color: '#fff',
      flex: 1,
      border: 'none',
      fontWeight: 700
    }
  }

  if (loading) {
    return (
      <div className="shift-timer-card skeleton" style={{ height: '140px' }}></div>
    )
  }

  return (
    <>
      <div className={`shift-timer-card ${isActive ? 'active' : ''} ${isFinished ? 'finished' : ''} ${isOnBreak ? 'on-break' : ''}`}>
        <div className="shift-timer-header">
          <div className="shift-timer-status">
            <span className={`shift-status-dot ${isOnBreak ? 'orange' : isActive ? 'pulse' : isFinished ? 'done' : ''}`}></span>
            <span className="shift-status-text">
              {isOnBreak ? 'На перерыве' : isActive ? 'Смена идёт' : isFinished ? 'Смена завершена' : 'Смена не начата'}
              {isBreakCritical && (
                <span className="break-warning-flash" style={{ marginLeft: '6px', display: 'inline-flex', verticalAlign: 'middle' }} title="Превышение времени перерыва!"><IconAlertCircle size={16} style={{ color: 'var(--warning)' }} /></span>
              )}
            </span>
          </div>
          {isFinished && shiftData?.hours && (
            <span className="shift-hours-badge">{shiftData.hours} ч.</span>
          )}
          {businessHours && (
            <span className="shift-schedule-info">
              {businessHours.open_time} — {businessHours.close_time}
            </span>
          )}
        </div>

        {isActive && (
          <>
            <div className="shift-timer-display">
              <div className="shift-timer-main-info" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <div className="shift-timer-time" title="Чистое рабочее время">
                  {formatTime(cleanSeconds)}
                </div>
                {isOnBreak && (
                  <div className={`shift-timer-break-time ${isBreakOvertime ? 'overtime' : ''}`} style={{ fontSize: '14px', color: isBreakOvertime ? 'var(--danger)' : 'var(--warning)', marginTop: '4px', fontWeight: 600 }} title="Время текущего перерыва">
                    {formatTime(breakElapsed)} {isBreakOvertime ? '(Превышение!)' : ''}
                  </div>
                )}
              </div>
              <div className="shift-timer-earnings">
                <span className="shift-earnings-label">Заработано</span>
                <span className="shift-earnings-value">{formatMoney(liveEarnings)}</span>
              </div>
            </div>
            {(todayOps.acceptance > 0 || todayOps.issued > 0) && (
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginTop: '6px' }}>
                {todayOps.acceptance > 0 && (
                  <span style={{ fontSize: '12px', color: 'var(--wb-primary)', fontWeight: 600, background: 'rgba(203,17,171,0.08)', padding: '3px 10px', borderRadius: '12px' }}>{todayOps.acceptance} шт.</span>
                )}
                {todayOps.issued > 0 && (
                  <span style={{ fontSize: '12px', color: 'var(--success)', fontWeight: 600, background: 'rgba(16,185,129,0.08)', padding: '3px 10px', borderRadius: '12px' }}>{todayOps.issued} шт.</span>
                )}
              </div>
            )}
            <div className="shift-timer-start-info" style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px', fontSize: '12px', color: 'var(--text-secondary)' }}>
              <span>Начало: {new Date(shiftData.started_at).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}</span>
              {breaksCount > 0 && (
                <span className="shift-timer-breaks-summary">
                  Перерывы: {breaksCount}/{maxBreaksCount} ({totalCompletedBreaksMinutes + Math.floor(breakElapsed / 60)} мин)
                </span>
              )}
            </div>
          </>
        )}

        {isFinished && (
          <div className="shift-timer-summary">
            <div>
              <span className="shift-summary-label">Начало</span>
              <span className="shift-summary-value">{new Date(shiftData.started_at).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}</span>
            </div>
            <div className="shift-summary-divider">→</div>
            <div>
              <span className="shift-summary-label">Конец</span>
              <span className="shift-summary-value">{new Date(shiftData.ended_at).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}</span>
            </div>
            <div className="shift-summary-divider">•</div>
            <div>
              <span className="shift-summary-label">Часы</span>
              <span className="shift-summary-value shift-summary-hours">{shiftData.hours} ч</span>
            </div>
          </div>
        )}

        <div className="shift-timer-actions" style={{ marginTop: '12px' }}>
          {!isActive && !isFinished && (
            <>
              {timeUntilOpen !== null ? (
                <div className="shift-timer-wait">
                  <span className="shift-wait-icon" style={{ display: 'inline-flex' }}><IconClock size={20} /></span>
                  <span className="shift-wait-text">
                    Смена откроется в {businessHours?.open_time}
                    <small> (через {timeUntilOpen} мин.)</small>
                  </span>
                </div>
              ) : (
                <button className="btn shift-btn-start" onClick={handleStart} disabled={starting}>
                  {starting ? <IconHourglass size={16} style={{ verticalAlign: 'middle' }} /> : <IconPlay size={16} style={{ verticalAlign: 'middle' }} />} {starting ? 'Начинаем...' : 'Начать смену'}
                </button>
              )}
            </>
          )}
          {isActive && (
            <div className="shift-timer-buttons-group" style={{ display: 'flex', gap: '10px', width: '100%' }}>
              {isOnBreak ? (
                <button 
                  className={`btn shift-btn-break-end ${isBreakCritical ? 'critical-pulse' : ''}`} 
                  style={getBreakBtnStyle()} 
                  onClick={handleEndBreak}
                >
                  Вернуться
                </button>
              ) : (
                <button 
                  className="btn shift-btn-break-start" 
                  style={{ flex: 1, background: 'var(--warning)', color: '#000', fontWeight: 600 }}
                  onClick={handleStartBreak} 
                  disabled={isBreaksLimitReached}
                  title={isBreaksLimitReached ? 'Достигнут лимит перерывов' : 'Выйти на перерыв'}
                >
                  {isBreaksLimitReached ? 'Перерыв (лимит)' : 'На перерыв'}
                </button>
              )}
              <button className="btn shift-btn-end" style={{ flex: 1 }} onClick={() => setShowEndConfirm(true)} disabled={ending}>
                {ending ? <IconHourglass size={16} style={{ verticalAlign: 'middle' }} /> : <IconStop size={16} style={{ verticalAlign: 'middle' }} />} {ending ? 'Завершить...' : 'Завершить'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Модалка подтверждения завершения смены — вместо confirm() */}
      <Modal isOpen={showEndConfirm} onClose={() => setShowEndConfirm(false)} title="Завершение смены" isBottomSheet={true} onConfirm={handleEnd}>
        <div style={{ padding: '10px 0 24px 0', color: 'var(--text-secondary)' }}>
          Вы уверены, что хотите завершить смену? После завершения повторное начало сегодня невозможно.
          {isOnBreak && (
            <div style={{ marginTop: '12px', color: 'var(--danger)', fontWeight: 'bold' }}>
              Внимание: вы всё еще находитесь на перерыве! Он будет автоматически завершен временем окончания смены.
            </div>
          )}
        </div>
        <div className="shift-timer-summary" style={{ marginBottom: '20px' }}>
          <div>
            <span className="shift-summary-label">Чистая работа</span>
            <span className="shift-summary-value shift-summary-hours">{formatTime(cleanSeconds)}</span>
          </div>
          <div className="shift-summary-divider">•</div>
          <div>
            <span className="shift-summary-label">Заработано</span>
            <span className="shift-summary-value">{formatMoney(liveEarnings)}</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setShowEndConfirm(false)}>
            Отмена
          </button>
          <button className="btn shift-btn-end" style={{ flex: 1 }} onClick={handleEnd} disabled={ending}>
            {ending ? <><IconHourglass size={16} style={{ verticalAlign: 'middle' }} /> Завершаем...</> : <><IconStop size={16} style={{ verticalAlign: 'middle' }} /> Завершить</>}
          </button>
        </div>
      </Modal>
    </>
  )
}
